import React, { useState } from "react";
import { Plus, ChevronDown, ChevronUp } from "lucide-react";
import { OutlineButton, PrimaryButton } from "../../../components/Buttons";
import { FormModal } from "../../../components/form";

/**
 * Pick how to insert a reply into a WhatsApp workflow: create a new template
 * from scratch, or re-use one of the previously saved replies. Not a Form —
 * it's a picker with two action paths.
 */
const UseReplyModal = ({
    open,
    onClose,
    onCreateNewReply,
    onUseExistingTemplate,
    previousTemplates,
    isLoadingWorkflows,
}) => {
    const [isPreviousWorkflowsOpen, setIsPreviousWorkflowsOpen] = useState(true);

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="Choose Reply Action"
            size="xl"
        >
            <p className="text-gray-600 text-sm mb-4">
                Create a new reply template or use an existing one
            </p>

            <PrimaryButton
                onClick={onCreateNewReply}
                className="mb-4 w-full sm:w-2/3 md:w-1/2 mx-auto h-10 sm:h-12 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
                <Plus size={18} />
                Create New Reply Template
            </PrimaryButton>

            <div className="border-t border-gray-200 my-3 sm:my-4 pt-3 sm:pt-4">
                <div
                    className="flex items-center justify-between mb-3 cursor-pointer"
                    onClick={() => setIsPreviousWorkflowsOpen(!isPreviousWorkflowsOpen)}
                >
                    <div className="font-bold text-sm sm:text-base">Existing Replies</div>
                    <div className="flex items-center gap-2">
                        <div className="bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-600">
                            {previousTemplates.length} templates
                        </div>
                        <button className="text-gray-500 hover:text-gray-700 transition-colors">
                            {isPreviousWorkflowsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                    </div>
                </div>

                {isPreviousWorkflowsOpen && (
                    <div className="pr-1 sm:pr-2">
                        {isLoadingWorkflows ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-flamingo-600" />
                                <span className="ml-3 text-gray-600">Loading replies...</span>
                            </div>
                        ) : previousTemplates.length === 0 ? (
                            <div className="text-gray-500 text-sm text-center p-6 sm:p-8 bg-gray-50 rounded-lg">
                                No replies found. Create a new reply template to get started.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 p-1">
                                {previousTemplates.map((template, idx) => (
                                    <div
                                        key={template.customerWhatsappTemplateId || idx}
                                        className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-white transition-all hover:-translate-y-0.5 hover:shadow-md flex flex-col h-full"
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="font-bold text-sm sm:text-[15px] text-gray-900 flex-1 mr-2 line-clamp-1">
                                                {template.friendlyName || template.templateName || `Reply ${idx + 1}`}
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 p-2 sm:p-3 rounded-md mb-3 text-xs sm:text-sm text-gray-700 border border-gray-200 flex-1 line-clamp-3">
                                            {template.components?.find((c) => c.type === "BODY")?.text || "No body"}
                                        </div>

                                        {template.components?.find((c) => c.type === "BUTTONS")?.buttons?.length > 0 && (
                                            <div className="mb-3 flex flex-wrap gap-1.5">
                                                {template.components
                                                    .find((c) => c.type === "BUTTONS")
                                                    ?.buttons.map((button, btnIdx) => (
                                                        <div
                                                            key={btnIdx}
                                                            className="bg-flamingo-100 px-2 py-1 rounded-md text-xs text-flamingo-600 border border-gray-200"
                                                        >
                                                            {button.text}
                                                        </div>
                                                    ))}
                                            </div>
                                        )}

                                        <OutlineButton
                                            onClick={() => onUseExistingTemplate(template)}
                                            className="w-full mt-auto text-sm sm:text-base py-2 sm:py-2.5"
                                        >
                                            Use this reply
                                        </OutlineButton>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </FormModal>
    );
};

export default UseReplyModal;
